import { useParams } from "react-router";
import t from "@/lang/lang";
import { useGfsAssessmentOverviewQuery } from "@/services/backend/gfs/assessment/api";
import {
  GfsAssessment,
  GfsAssessmentOverview,
} from "@/services/backend/gfs/assessment/types";
import { formatGfsUnit } from "@/services/backend/gfs/unit";
import {
  formatDuration,
  formatPrice,
} from "@/services/backend/pestcontrol/lib/monitor";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";

export function GfsAssessmentOverviewPage() {
  const { assessmentId = "" } = useParams();
  const { data, error, isLoading } = useGfsAssessmentOverviewQuery(
    { assessmentId },
    { skip: !assessmentId },
  );

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading || !data) {
    return (
      <div className="text-muted-foreground p-4 text-sm">
        {t("Lade Übersicht ...")}
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-16">
      <Card>
        <CardHeader>
          <CardTitle>{t("Übersicht")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <OverviewValue label={t("Betreff")} value={data.subject || "-"} />
            <OverviewValue
              label={t("Befallsstärke")}
              value={formatInfestationLevel(data.infestationLevel)}
            />
            <OverviewValue
              label={t("Arbeitszeit gesamt")}
              value={formatDuration(data.workEstimate.duration)}
            />
            <OverviewValue
              label={t("Techniker")}
              value={String(data.workEstimate.technicians)}
            />
            <OverviewValue
              label={t("Fahrzeit")}
              value={formatDuration(data.workEstimate.travelDuration)}
            />
            <OverviewValue
              label={t("Anfahrten")}
              value={String(data.workEstimate.trips ?? 0)}
            />
            <OverviewValue
              label={t("Maßnahmen")}
              value={String(data.workEstimate.measureCount ?? 0)}
            />
            <OverviewValue
              label={t("Maßnahmenzeitraum")}
              value={formatMeasurePeriod(data)}
            />
          </section>

          <section className="grid gap-4 border-t pt-5 md:grid-cols-2">
            <OverviewValue
              label={t("Schädlingskategorien")}
              value={formatList(data.pestCategories.map((pest) => pest.name))}
            />
            <OverviewValue
              label={t("Behandlungsmethoden")}
              value={formatList(
                data.treatmentMethods.map((method) => method.name),
              )}
            />
          </section>
        </CardContent>
      </Card>

      <OverviewAreas areas={data.areas} />
      <OverviewMonitors monitors={data.monitors} />
      <OverviewMaterials materials={data.materials} />
    </div>
  );
}

function OverviewAreas({ areas }: Pick<GfsAssessmentOverview, "areas">) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Ausgewählte Bereiche")}</CardTitle>
      </CardHeader>
      <CardContent>
        {areas.length === 0 ? (
          <EmptyState text={t("Keine Bereiche ausgewählt")} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Gebäude")}</TableHead>
                <TableHead>{t("Ebene")}</TableHead>
                <TableHead>{t("Bereich")}</TableHead>
                <TableHead>{t("Fläche")}</TableHead>
                <TableHead>{t("Arbeitszeit")}</TableHead>
                <TableHead>{t("Maßnahmen")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas.map((area) => (
                <TableRow key={area.areaId}>
                  <TableCell>{area.buildingName || "-"}</TableCell>
                  <TableCell>{area.levelName || "-"}</TableCell>
                  <TableCell>{area.areaName || area.areaId}</TableCell>
                  <TableCell>
                    {area.areaSizeM2
                      ? `${area.areaSizeM2.toLocaleString("de-DE")} m²`
                      : "-"}
                  </TableCell>
                  <TableCell>{formatDuration(area.laborDuration)}</TableCell>
                  <TableCell>
                    {formatList(area.measures.map((measure) => measure.name))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewMonitors({
  monitors,
}: Pick<GfsAssessmentOverview, "monitors">) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Monitore")}</CardTitle>
      </CardHeader>
      <CardContent>
        {monitors.length === 0 ? (
          <EmptyState text={t("Keine Monitore ausgewählt")} />
        ) : (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[15%]">{t("Gebäude")}</TableHead>
                <TableHead className="w-[10%]">{t("Ebene")}</TableHead>
                <TableHead className="w-[14%]">{t("Bereich")}</TableHead>
                <TableHead className="w-[17%]">{t("Monitor")}</TableHead>
                <TableHead className="w-[10%]">{t("Typ")}</TableHead>
                <TableHead className="w-[8%] text-right">
                  {t("Anzahl")}
                </TableHead>
                <TableHead className="w-[26%]">{t("Bemerkung")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monitors.map((entry, index) => (
                <TableRow
                  key={`${entry.areaId}-${entry.monitor.catalogItemId}-${index}`}
                >
                  <TableCell>{entry.buildingName || "-"}</TableCell>
                  <TableCell>{entry.levelName || "-"}</TableCell>
                  <TableCell>{entry.areaName || entry.areaId}</TableCell>
                  <TableCell>{entry.monitor.name || "-"}</TableCell>
                  <TableCell>{entry.monitor.type || "-"}</TableCell>
                  <TableCell className="text-right">
                    {entry.monitor.amount}
                  </TableCell>
                  <TableCell className="max-w-0 align-top whitespace-normal">
                    <div className="max-w-full overflow-hidden break-words whitespace-pre-wrap">
                      {entry.monitor.comment || "-"}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewMaterials({
  materials,
}: Pick<GfsAssessmentOverview, "materials">) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Verbrauchsmaterialien")}</CardTitle>
      </CardHeader>
      <CardContent>
        {materials.length === 0 ? (
          <EmptyState text={t("Keine Verbrauchsmaterialien ausgewählt")} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Gebäude")}</TableHead>
                <TableHead>{t("Ebene")}</TableHead>
                <TableHead>{t("Bereich")}</TableHead>
                <TableHead>{t("Verbrauchsmaterial")}</TableHead>
                <TableHead className="text-right">{t("Menge")}</TableHead>
                <TableHead>{t("Einheit")}</TableHead>
                <TableHead className="text-right">
                  {t("Preis je Einheit")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {materials.map((entry, index) => (
                <TableRow
                  key={`${entry.areaId ?? "assessment"}-${entry.material.sourceId ?? entry.material.key}-${index}`}
                >
                  <TableCell>{entry.buildingName || "-"}</TableCell>
                  <TableCell>{entry.levelName || "-"}</TableCell>
                  <TableCell>{entry.areaName || t("Gesamt")}</TableCell>
                  <TableCell>{entry.material.name || "-"}</TableCell>
                  <TableCell className="text-right">
                    {entry.material.quantity.toLocaleString("de-DE")}
                  </TableCell>
                  <TableCell>{formatGfsUnit(entry.material.unit)}</TableCell>
                  <TableCell className="text-right">
                    {formatPrice(entry.material.unitPrice)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-sm">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="text-muted-foreground text-sm">{text}</div>;
}

function formatInfestationLevel(
  value: GfsAssessment["infestationLevel"],
): string {
  switch (value) {
    case "light":
      return t("Leicht");
    case "medium":
      return t("Mittel");
    case "heavy":
      return t("Stark");
    default:
      return t("Nicht angegeben");
  }
}

function formatMeasurePeriod(data: GfsAssessmentOverview): string {
  if (!data.workEstimate.measurePeriodUnit) return "-";
  const units = {
    hours: t("Stunden"),
    days: t("Tage"),
    weeks: t("Wochen"),
    months: t("Monate"),
  } as const;
  return `${data.workEstimate.measurePeriodValue ?? 0} ${units[data.workEstimate.measurePeriodUnit]}`;
}

function formatList(values: string[]): string {
  return values.length > 0 ? values.join(", ") : "-";
}
